import {
  getSJVCCampusByZip as _sjvcGetCampusByZip,
  getSJVCProgramsForCampus as _sjvcGetProgramsForCampus,
} from './sjvcData'
import {
  getCarringtonCampusByZip as _carringtonGetCampusByZip,
  getCarringtonProgramsForCampus as _carringtonGetProgramsForCampus,
  getCarringtonCampusDirectory as _carringtonGetCampusDirectory,
  CARRINGTON_PROGRAMS as _carringtonPrograms,
} from './carringtonData'

export interface UniversityProgram {
  name: string
  category: string
  degreeLevel?: string
  slug?: string
}

export interface UniversityFeature {
  icon: string
  label: string
  description: string
  superscript?: string
}

export interface UniversityTestimonial {
  quote: string
  name: string
  program: string
}

export interface UniversityData {
  slug: string
  name: string
  shortName: string
  tagline: string
  heroTagline: string
  mission: string
  primaryColor: string
  secondaryColor: string
  ctaColor: string
  logoText: string
  logoUrl?: string
  heroImageUrl?: string
  phoneNumber?: string
  classesStartDate?: string
  programs: UniversityProgram[]
  degreeLevels: string[]
  features: UniversityFeature[]
  testimonial?: UniversityTestimonial
  mobileSection?: {
    heading: string
    description: string
  }
  accreditation: string
  founded: string
  type: string
  tcpaText?: string
  // Optional second TCPA disclosure (e.g. SMS opt-in shown as a separate checkbox)
  tcpaTextSms?: string
  // Required advertiser/lead routing identifiers (per-partner config)
  mPubID?: string
  mPubCampaignID?: string
  mBrandID?: string
  // Optional override for the lead post endpoint
  leadPostUrl?: string
  // Campus-aware advertisers (e.g. SJVC): provide a zip→campus lookup and
  // a programs-for-campus resolver. When present, the form prepends a
  // step 0 that asks the user for their zip to determine campus + programs.
  campusZipLookup?: (zip: string) => { id: number; name: string } | null
  programsForCampus?: (campusId: number) => UniversityProgram[]
  // Optional landing-page content rendered on the first step of the
  // advertiser form to lift CTR. Includes a benefit-driven headline,
  // supporting paragraph, four feature blurbs, and a closing career
  // section with copy + image.
  // Optional hero background image, overlaid on the primary color band.
  heroBackgroundImage?: string
  landingContent?: {
    headline: string
    subheadline: string
    features: { icon: string; title: string; description: string }[]
    stats?: { value: string; label: string }[]
    careerSection?: {
      headline: string
      body: string
      imageUrl: string
      imageAlt?: string
    }
    testimonial?: { quote: string; name: string; program: string }
    infoSections?: {
      eyebrow?: string
      heading: string
      body: string
      bullets?: string[]
    }[]
    finalCta?: { headline: string; body: string; buttonLabel?: string }
    // Optional community / lifestyle image rendered in its own section.
    communityImage?: {
      url: string
      alt?: string
      eyebrow?: string
      heading?: string
      body?: string
    }
    // Optional campus directory listing (campus name + programs offered).
    campusList?: { id: number; name: string; programs: string[] }[]
  }
}

export const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')

export const getProgramBySlug = (
  university: UniversityData,
  programSlug: string,
) => {
  return university.programs.find(
    (p) => (p.slug || slugify(p.name)) === programSlug,
  )
}

export const universities: Record<string, UniversityData> = {
  'colorado-technical-university': {
    slug: 'colorado-technical-university',
    name: 'Colorado Technical University',
    shortName: 'CTU',
    tagline: 'Pursue a degree without putting your life on hold',
    heroTagline: 'Pursue a degree without putting your life on hold',
    mission:
      'Our mission is to provide industry-relevant higher education to a diverse student population through innovative technology and experienced faculty, enabling the pursuit of personal and professional goals.',
    primaryColor: '#991B1B',
    secondaryColor: '#1a1a1a',
    ctaColor: '#B91C1C',
    logoText: 'Colorado Technical University',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/6a2ATeMy5q3btLrv6d2QP1/CTU_logo._webp.webp',
    heroImageUrl:
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&h=800&fit=crop&q=80',
    phoneNumber: '855-204-0444',
    classesStartDate: 'May 5, 2026',
    degreeLevels: ["Associate's", "Bachelor's", "Master's", 'Doctoral'],
    programs: [
      { name: 'MBA', category: 'Business', degreeLevel: "Master's" },
      { name: 'Management', category: 'Business', degreeLevel: "Bachelor's" },
      { name: 'Accounting', category: 'Business', degreeLevel: "Bachelor's" },
      { name: 'Marketing', category: 'Business', degreeLevel: "Bachelor's" },
      {
        name: 'Project Management',
        category: 'Business',
        degreeLevel: "Master's",
      },
      { name: 'Computer Science', category: 'IT', degreeLevel: "Bachelor's" },
      { name: 'Cybersecurity', category: 'IT', degreeLevel: "Bachelor's" },
      {
        name: 'Software Engineering',
        category: 'IT',
        degreeLevel: "Bachelor's",
      },
      { name: 'Data Analytics', category: 'IT', degreeLevel: "Bachelor's" },
      { name: 'IT Management', category: 'IT', degreeLevel: "Master's" },
      {
        name: 'Healthcare Management',
        category: 'Healthcare',
        degreeLevel: "Master's",
      },
      {
        name: 'Health Informatics',
        category: 'Healthcare',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Criminal Justice',
        category: 'Criminal Justice',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Homeland Security',
        category: 'Criminal Justice',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Electrical Engineering',
        category: 'Engineering',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Computer Engineering',
        category: 'Engineering',
        degreeLevel: "Bachelor's",
      },
    ],
    features: [
      {
        icon: 'ArrowLeftRightIcon',
        label: 'Transfer Credits',
        description: 'Transfer in your eligible credits',
        superscript: '1',
      },
      {
        icon: 'FileCheckIcon',
        label: 'Financial Aid',
        description: 'Potential funding options available',
      },
      {
        icon: 'TimerIcon',
        label: 'CTU FastTrack™',
        description: 'Earn college credit for what you already know',
        superscript: '2',
      },
      {
        icon: 'AwardIcon',
        label: 'Academic Excellence',
        description: 'Study with instructors who are experienced professionals',
      },
    ],
    testimonial: {
      quote:
        'I had an awesome journey here at CTU. I chose CTU due the convenience of on line classes. This was such an amazing program. The professors were amazing and were all the steps of the way. I also loved my Success coach who was always available! CTU has the best online programs and was great experience!',
      name: 'Catherine J.',
      program: 'Master of Science in Healthcare Management 2024',
    },
    mobileSection: {
      heading: 'Access Classes from Your Phone',
      description:
        "CTU embraces new technologies that innovate and enhance the learning experience by offering increased connectivity and the freedom to choose where, when and how you study. That's why we are proud to bring you CTU Mobile, a unique educational experience in the palm of your hand. With our state-of-the-art mobile app, you can access your courses anywhere and work toward achieving your academic goals on your own time.",
    },
    accreditation: 'HLC Accredited',
    founded: '1965',
    type: 'Private, For-Profit',
    tcpaText:
      'By checking this box, I consent to receive calls and text messages from Colorado Technical University (CTU) at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.',
  },
  'university-of-arizona-global-campus': {
    slug: 'university-of-arizona-global-campus',
    name: 'University of Arizona Global Campus',
    shortName: 'UAGC',
    tagline: 'Education tailored to your life.',
    heroTagline: 'Education tailored to your life.',
    mission:
      'To provide high-quality, accessible, and innovative educational programs.',
    primaryColor: '#CC0033',
    secondaryColor: '#1a1a1a',
    ctaColor: '#CC0033',
    logoText: 'University of Arizona Global Campus',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/geonUNDFA7aKRwGy5reWVE/UAGC_LOGO_-_v1.webp',
    accreditation: 'WSCUC Accredited',
    founded: '2020',
    type: 'Public',
    degreeLevels: ["Associate's", "Bachelor's", "Master's", 'Doctoral'],
    programs: [
      {
        name: 'Business Administration',
        category: 'Business',
        degreeLevel: "Bachelor's",
      },
      { name: 'Accounting', category: 'Business', degreeLevel: "Bachelor's" },
      {
        name: 'Information Technology',
        category: 'IT',
        degreeLevel: "Bachelor's",
      },
      { name: 'Cybersecurity', category: 'IT', degreeLevel: "Master's" },
      {
        name: 'Health Care Administration',
        category: 'Healthcare',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Nursing (RN to BSN)',
        category: 'Healthcare',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Early Childhood Education',
        category: 'Education',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Special Education',
        category: 'Education',
        degreeLevel: "Master's",
      },
    ],
    features: [],
    tcpaText:
      'By checking this box, I consent to receive calls and text messages from the University of Arizona Global Campus (UAGC) and its representatives at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply. I may withdraw my consent at any time.',
  },
  'san-joaquin-valley-college': {
    slug: 'san-joaquin-valley-college',
    name: 'San Joaquin Valley College',
    shortName: 'SJVC',
    tagline: 'A Private Career College',
    heroTagline: 'A Private Career College',
    mission:
      'San Joaquin Valley College prepares students for skilled, hands-on careers through focused, accelerated diploma and degree programs.',
    primaryColor: '#1E78C2',
    secondaryColor: '#0F4F8B',
    ctaColor: '#22A55C',
    logoText: 'San Joaquin Valley College',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/h4UizCs35BQrDroCjKSA7H/SJVC_logo.svg',
    accreditation: 'ACCSC Accredited',
    founded: '1977',
    type: 'Private Career College',
    degreeLevels: ['Diploma'],
    programs: [
      {
        name: 'Electrical Technology',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'electrical-technology',
      },
      {
        name: 'Heating, Ventilation, AC and Refrigeration',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'hvac',
      },
      {
        name: 'Maintenance Technician',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'maintenance-technician',
      },
      {
        name: 'Construction Management',
        category: 'Skilled Trades',
        degreeLevel: 'Diploma',
        slug: 'construction-management',
      },
    ],
    features: [],
    heroBackgroundImage:
      'https://cdn.magicpatterns.com/uploads/2974HoVszAU37LFnoFezRY/ChatGPT_Image_May_5,_2026,_03_56_38_PM.png',
    tcpaTextSms: 'Allow text messages from San Joaquin Valley College.',
    tcpaText:
      'By submitting this form, I expressly authorize San Joaquin Valley College, its subsidiaries, affiliates, agents and contractors to contact me regarding educational services via email, telephone (including cellular) or text message using automated technology at the email address and phone numbers provided. I understand this consent is not required to attend San Joaquin Valley College. I understand message and data rates may apply, and that my consent may be revoked at any time by notifying San Joaquin Valley College. View our privacy policy. Visit https://www.sjvc.edu/admissions/consumer-information/ for important information on program outcomes.',
    campusZipLookup: (zip: string) => {
      const campus = _sjvcGetCampusByZip(zip)
      return campus ? { id: campus.id, name: campus.name } : null
    },
    programsForCampus: (campusId: number) =>
      _sjvcGetProgramsForCampus(campusId),
    landingContent: {
      headline: 'Train for a Hands-On Career You Can Be Proud Of',
      subheadline:
        'San Joaquin Valley College helps motivated students gain real-world skills through focused, accelerated programs taught by experienced industry professionals — so you can graduate ready to work.',
      features: [
        {
          icon: 'BrainIcon',
          title: 'Real-World Skills',
          description:
            'Hands-on training and industry-experienced instructors prepare you for the work you want to do.',
        },
        {
          icon: 'AwardIcon',
          title: 'Start Your Path',
          description:
            'Choose from diploma, certificate, and associate degree programs designed to get you career-ready.',
        },
        {
          icon: 'LaptopIcon',
          title: 'Flexible Learning',
          description:
            'Learn on a schedule that works for your life with day, evening, and online program options.',
        },
        {
          icon: 'DollarSignIcon',
          title: 'Affordable Tuition',
          description:
            'Financial aid is available for those who qualify. Tuition varies by program and campus.',
        },
      ],
      careerSection: {
        headline: 'Preparing for a New Career. Anytime, Anywhere.',
        body: "We know you have what it takes. That's why we're here to help you reach your full potential. SJVC offers programs that can prepare you for a diverse range of professional careers — without putting your life on hold.",
        imageUrl:
          'https://cdn.magicpatterns.com/uploads/v7tdNYfNE9U241JHrKMNap/SJVC_-_lower_image.webp',
        imageAlt: 'SJVC Electrical Technology student with tools',
      },
      communityImage: {
        url: 'https://cdn.magicpatterns.com/uploads/vVg3Z16qLb1rRZSB5BHdwH/Certificate-and-Associate-Degree-Programs-Hero.webp',
        alt: 'SJVC students from Electrical Technology and Medical programs',
        eyebrow: 'Join the SJVC Community',
        heading: 'A community built for students who want more.',
        body: "From day one, SJVC students are surrounded by instructors, advisors, and classmates who are invested in their success. Whether you're heading into healthcare, the trades, or business, you'll graduate with the skills, confidence, and connections to thrive.",
      },
      campusList: [
        {
          id: 33,
          name: 'Bakersfield',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        {
          id: 36,
          name: 'Fresno TEC',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        {
          id: 38,
          name: 'Hesperia',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        { id: 39, name: 'Lancaster', programs: ['HVAC & Refrigeration'] },
        {
          id: 41,
          name: 'Modesto',
          programs: ['Electrical Technology', 'Maintenance Technician'],
        },
        {
          id: 43,
          name: 'Ontario',
          programs: [
            'Electrical Technology',
            'HVAC & Refrigeration',
            'Maintenance Technician',
            'Construction Management',
          ],
        },
        {
          id: 45,
          name: 'Rancho Mirage',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        { id: 46, name: 'Santa Maria', programs: [] },
        {
          id: 47,
          name: 'Temecula',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        {
          id: 48,
          name: 'Visalia',
          programs: [
            'Electrical Technology',
            'HVAC & Refrigeration',
            'Maintenance Technician',
          ],
        },
        { id: 220, name: 'San Leandro', programs: ['Electrical Technology'] },
        {
          id: 222,
          name: 'Phoenix TEC',
          programs: ['Electrical Technology', 'HVAC & Refrigeration'],
        },
        { id: 223, name: 'Sacramento', programs: ['Electrical Technology'] },
      ],
      stats: [
        { value: '1977', label: 'Educating since' },
        { value: '13', label: 'Campus locations' },
        { value: '50+', label: 'Career-focused programs' },
        { value: 'ACCSC', label: 'Nationally accredited' },
      ],
      testimonial: {
        quote:
          'SJVC gave me real, hands-on training and instructors who actually cared. I went from unsure about my future to confidently working in my field within months of graduating.',
        name: 'Maria G.',
        program: 'SJVC Graduate',
      },
      infoSections: [
        {
          eyebrow: 'How SJVC Works',
          heading:
            'A focused path designed around your career — not the other way around.',
          body: "SJVC programs are built around the skills employers actually look for. You'll learn from instructors with real industry experience and graduate with the practical know-how to step into your new role on day one.",
          bullets: [
            'Accelerated diploma, certificate, and associate degree options',
            'Hands-on labs and equipment that mirror real workplaces',
            'Dedicated career services to help you land your first job',
            'Day, evening, and online schedules to fit your life',
          ],
        },
        {
          eyebrow: 'Accreditation & Outcomes',
          heading: 'A trusted name in career education for nearly 50 years.',
          body: 'San Joaquin Valley College is institutionally accredited by the Accrediting Commission of Career Schools and Colleges (ACCSC) and has helped tens of thousands of students launch new careers. Visit sjvc.edu/admissions/consumer-information for important information on program outcomes.',
          bullets: [
            'ACCSC-accredited institution',
            'Financial aid available for those who qualify',
            'Career-services support before and after graduation',
            'Programs aligned with regional employer needs',
          ],
        },
      ],
      finalCta: {
        headline: 'Ready to take the next step?',
        body: 'Tell us a little about you and a friendly SJVC admissions advisor will reach out with the program details and next steps.',
        buttonLabel: 'Request Information',
      },
    },
  },
  'carrington-college': {
    slug: 'carrington-college',
    name: 'Carrington College',
    shortName: 'Carrington',
    tagline: 'Career-focused healthcare education',
    heroTagline: 'Career-focused healthcare education',
    mission:
      'Carrington College helps motivated students gain real-world skills through hands-on healthcare and career programs taught by experienced industry professionals.',
    primaryColor: '#1E3A6F',
    secondaryColor: '#15294F',
    ctaColor: '#1E3A6F',
    logoText: 'Carrington College',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/3wXCmBZEYmszo4mCNS3nbM/image_26_(1).png',
    accreditation: 'ACCSC Accredited',
    founded: '1967',
    type: 'Private Career College',
    degreeLevels: ['Diploma', 'Undergraduate'],
    programs: _carringtonPrograms.map((p) => ({
      name: p.name,
      category: p.category,
      degreeLevel: p.degreeLevel,
      slug: p.slug,
    })),
    features: [],
    heroBackgroundImage:
      'https://cdn.magicpatterns.com/uploads/njz4YLzzbXZLg4BaPf9equ/3e226cfb-0665-43b5-891f-bbb6f803acf3.png',
    tcpaTextSms: 'Allow for text messages from Carrington College.',
    tcpaText:
      'By submitting this form, I expressly authorize Carrington College, and its parent company San Joaquin Valley College, Inc., their subsidiaries, affiliates, agents, and contractors to contact me regarding educational services via email, telephone (including cellular) or text message – using automated technology – at the email address and phone numbers provided. I understand this consent is not required to attend the college. I understand message and data rates may apply, and that my consent may be revoked at any time by notifying the college. View our privacy policy. Visit https://carrington.edu/admissions/student-consumer-information/ for important information on program outcomes.',
    campusZipLookup: (zip: string) => {
      const campus = _carringtonGetCampusByZip(zip)
      return campus ? { id: campus.id, name: campus.name } : null
    },
    programsForCampus: (campusId: number) =>
      _carringtonGetProgramsForCampus(campusId),
    landingContent: {
      headline: 'Train for a Healthcare Career You Can Be Proud Of',
      subheadline:
        'Carrington College helps motivated students earn their certificate or degree in as few as 9 months — with hands-on training, industry-experienced instructors, and 27 campus locations across the Western U.S.',
      features: [
        {
          icon: 'AwardIcon',
          title: 'Hands-On Classes',
          description:
            'Qualified instructors lead hands-on classes to give you real-world experience.',
        },
        {
          icon: 'GraduationCapIcon',
          title: 'Convenient Locations',
          description:
            'Choose from online courses or study in person at one of our 27 campus locations across the Western U.S.',
        },
        {
          icon: 'LaptopIcon',
          title: 'Career Services',
          description:
            'From career planning to job placement, focused on helping you succeed.',
        },
        {
          icon: 'DollarSignIcon',
          title: 'Fast Track Learning',
          description:
            'Carrington College graduates earn their certificate in as few as 9 months.',
        },
      ],
      careerSection: {
        headline: 'Online and On-Campus Programs are Open for Enrollment',
        body: 'Why Wait… Make the First Step to a Fresh Start Today! Talk to an enrollment services representative about your options and earn your certificate in as few as 9–15 months.',
        imageUrl:
          'https://cdn.magicpatterns.com/uploads/rbjm4i8WSqPhF7HWW1drgE/ChatGPT_Image_May_6,_2026,_12_04_25_PM.png',
        imageAlt:
          'Carrington College student in scrubs holding books in a clinical classroom',
      },
      communityImage: {
        url: 'https://cdn.magicpatterns.com/uploads/x9njmQHgCyT3F5wuX1xydw/Carrington-College-HP-Hero-2026.webp',
        alt: 'Carrington College students in scrubs',
        eyebrow: 'Join the Carrington Community',
        heading: 'A community built for healthcare careers.',
        body: "From day one, Carrington students are surrounded by instructors, advisors, and classmates invested in their success. Whether you're heading into nursing, medical assisting, dental, or veterinary fields, you'll graduate with the skills, confidence, and connections to thrive.",
      },
      stats: [
        { value: '1967', label: 'Educating since' },
        { value: '27', label: 'Campus locations' },
        { value: '9 mo', label: 'Programs as fast as' },
        { value: 'ACCSC', label: 'Nationally accredited' },
      ],
      testimonial: {
        quote:
          'Carrington gave me real, hands-on training and instructors who actually cared. I went from unsure about my future to confidently working in my field within months of graduating.',
        name: 'Jasmine R.',
        program: 'Carrington College Graduate',
      },
      infoSections: [
        {
          eyebrow: 'How Carrington Works',
          heading:
            'A focused path designed around your healthcare career — not the other way around.',
          body: "Carrington programs are built around the skills employers actually look for. You'll learn from instructors with real industry experience and graduate with the practical know-how to step into your new role on day one.",
          bullets: [
            'Accelerated certificate and associate degree options',
            'Hands-on labs and equipment that mirror real clinical settings',
            'Dedicated career services to help you land your first job',
            'Online and on-campus schedules to fit your life',
          ],
        },
        {
          eyebrow: 'Accreditation & Outcomes',
          heading: 'A trusted name in career education for over 55 years.',
          body: 'Carrington College is institutionally accredited by the Accrediting Commission of Career Schools and Colleges (ACCSC) and has helped tens of thousands of students launch new careers in healthcare and beyond.',
          bullets: [
            'ACCSC-accredited institution',
            'Financial aid available for those who qualify',
            'Career-services support before and after graduation',
            '27 campus locations across the Western U.S.',
          ],
        },
      ],
      finalCta: {
        headline: 'Ready to take the next step?',
        body: 'Tell us a little about you and a friendly Carrington admissions advisor will reach out with program details and next steps.',
        buttonLabel: 'Talk to Us',
      },
      campusList: _carringtonGetCampusDirectory(),
    },
  },
}

export const getUniversityBySlug = (slug: string) => universities[slug]
